import { detectHost, integrationApi, type Integration } from "@repo/api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ALLOWED_INTEGRATION_TYPES } from "../constants/integrationTypes";
import { useRouting } from "../contexts/RoutingContext";
import { mockIntegrationsData } from "../mocks/integrationsMockData";

const PAGE_SIZE = 20;

interface TransformedIntegration {
  id: string;
  name: string;
  application: string;
  status: "Active" | "Failed" | "Pending";
  isEnabled?: boolean;
}

export interface IntegrationWithRawData extends TransformedIntegration {
  rawData: Integration;
}

/**
 * Transform API response to match UI structure
 */
const transformIntegrations = (
  integrations: Integration[],
): IntegrationWithRawData[] => {
  return integrations.map((integration) => {
    // Map connectionStatus to status
    let status: "Active" | "Failed" | "Pending" = "Active";
    if (integration.connectionStatus === "INVALID") {
      status = "Failed";
    } else if (integration.connectionStatus === "UNKNOWN") {
      status = "Pending";
    } else if (integration.connectionStatus === "VALID") {
      status = "Active";
    }

    return {
      id: integration.id.toString(),
      name: integration.displayName,
      application: integration.integrationType,
      status,
      isEnabled: integration.isEnabled,
      rawData: integration,
    };
  });
};

/**
 * React Query hook to fetch integrations
 * Uses mock data in standalone mode when APIs are not available
 */
export function useIntegrations(
  integrationType: string | null = ALLOWED_INTEGRATION_TYPES[0],
  connectionStatus?: string | null,
  sortField?: string,
  sortDirection?: string,
  displayName?: string | null,
) {
  // Safely get routing context - handle case where it might not be available
  let isStandaloneMode = false;
  try {
    const routing = useRouting();
    isStandaloneMode = routing?.isStandaloneMode ?? false;
  } catch (error) {
    // If RoutingContext is not available, default to MFE mode
    console.warn("RoutingContext not available, defaulting to MFE mode", error);
    isStandaloneMode = false;
  }

  // On ispm host, send integrationType only when the user has picked a Connection Source.
  // Other hosts retain the legacy default (first allowed type) so behavior there is unchanged.
  const apiIntegrationType =
    integrationType ??
    (detectHost() === "ispm" ? undefined : ALLOWED_INTEGRATION_TYPES[0]);

  // Map UI connection status values to API values
  // UI: active -> API: VALID, failed -> INVALID, pending -> UNKNOWN
  const apiConnectionStatus = connectionStatus
    ? connectionStatus === "active"
      ? "VALID"
      : connectionStatus === "failed"
        ? "INVALID"
        : connectionStatus === "pending"
          ? "UNKNOWN"
          : undefined
    : undefined;

  // Only include displayName in query if it has 3+ characters
  const apiDisplayName =
    displayName && displayName.trim().length >= 3
      ? displayName.trim()
      : undefined;

  return useInfiniteQuery({
    queryKey: [
      "integrations",
      isStandaloneMode,
      apiIntegrationType,
      apiConnectionStatus,
      sortField,
      sortDirection,
      apiDisplayName,
    ],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      // Use mock data in standalone mode
      if (isStandaloneMode) {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 500));
        let mockData = mockIntegrationsData.integrations as Integration[];
        // Filter to the requested integration type when one was provided.
        if (apiIntegrationType) {
          const upperType = apiIntegrationType.toUpperCase();
          mockData = mockData.filter(
            (integration) =>
              integration.integrationType.toUpperCase() === upperType,
          );
        }
        // Apply client-side filtering for connectionStatus in standalone mode
        if (apiConnectionStatus) {
          mockData = mockData.filter(
            (integration) =>
              integration.connectionStatus === apiConnectionStatus,
          );
        }
        // Apply client-side filtering for displayName in standalone mode
        if (apiDisplayName) {
          const searchLower = apiDisplayName.toLowerCase();
          mockData = mockData.filter((integration) =>
            integration.displayName?.toLowerCase().includes(searchLower),
          );
        }
        // Paginate mock data using token-based pagination
        const pageIndex = pageParam ? parseInt(pageParam, 10) : 0;
        const start = pageIndex * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        const paginatedData = mockData.slice(start, end);
        return {
          integrations: transformIntegrations(paginatedData),
          nextPageToken:
            end < mockData.length ? String(pageIndex + 1) : undefined,
        };
      }

      // Use real API in MFE mode
      try {
        const response = await integrationApi.getIntegrations({
          integrationType: apiIntegrationType,
          connectionStatus: apiConnectionStatus,
          sortField: sortField,
          sortDirection: sortDirection,
          displayName: apiDisplayName,
          onlyEnabled: false,
          nextPageToken: pageParam,
          pageSize: PAGE_SIZE,
        });
        return {
          integrations: transformIntegrations(response.integrations),
          nextPageToken: response.nextPageToken,
        };
      } catch (error) {
        console.error("Failed to fetch integrations in MFE mode:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: isStandaloneMode ? false : 1, // Don't retry in standalone mode
  });
}
